<template>
  <div class="view profile">
    <div v-if="!loggedIn">
      <UserLogin />
    </div>

    <template v-if="loggedIn && user">
      <header class="profile-header">
        <img :src="userAvatar" :alt="$t('account.dashboard.avatarAlt')" />
        <div class="who">
          <h1>{{ user.model.username }}</h1>
          <p class="muted">{{ user.model.email }}</p>
        </div>
        <router-link :to="$lp('/settings')" class="icon-btn" :aria-label="$t('account.dashboard.settings')">
          <i class="fas fa-sliders" aria-hidden="true"></i>
        </router-link>
      </header>

      <dl class="stats">
        <div class="stat"><i class="fas fa-pen-to-square" aria-hidden="true"></i><dd>{{ reportSubmissions ? reportSubmissions.length : 0 }}</dd><dt>{{ $t('account.dashboard.statReports') }}</dt></div>
        <div class="stat"><i class="fas fa-location-dot" aria-hidden="true"></i><dd>{{ cities ? cities.length : 0 }}</dd><dt>{{ $t('account.dashboard.statCities') }}</dt></div>
        <div class="stat"><i class="fas fa-award" aria-hidden="true"></i><dd>{{ userPoints }}</dd><dt>{{ $t('account.dashboard.statPoints') }}</dt></div>
      </dl>

      <section class="level card" aria-labelledby="level-h">
        <div class="level-head">
          <h2 id="level-h">{{ $t('account.dashboard.levelTitle', { n: level.index + 1, name: $t('account.dashboard.levels.' + level.key) }) }}</h2>
          <span v-if="level.next" class="muted">{{ $t('account.dashboard.levelToGo', { n: level.toNext }) }}</span>
        </div>
        <div class="track" role="progressbar" :aria-valuenow="Math.round(level.progress * 100)" aria-valuemin="0" aria-valuemax="100" :aria-label="$t('account.dashboard.levelAria')">
          <div class="fill" :style="{ width: Math.round(level.progress * 100) + '%' }"></div>
        </div>
        <ul class="steps">
          <li v-for="(l, i) in LEVELS" :key="l.key" :class="{ on: i <= level.index }">{{ $t('account.dashboard.levels.' + l.key) }} {{ l.min }}</li>
        </ul>
        <p class="muted">{{ $t('account.dashboard.levelNote') }}</p>
        <div v-if="pointsLog.length" class="recent-points">
          <h3>{{ $t('account.dashboard.recentPoints') }}</h3>
          <ul>
            <li v-for="e in pointsLog" :key="e.t + e.sid">
              <router-link v-if="e.sid" :to="{ name: 'ViewSubmission', params: { lang: $i18n.locale, id: e.sid } }">{{ e.sid }}</router-link>
              <span class="muted">{{ formatDate(e.t) }}</span>
              <strong>+{{ e.p }}</strong>
            </li>
          </ul>
        </div>
      </section>

      <p v-if="reportSubmissions && reportSubmissions.length" class="thanks">{{ $t('account.dashboard.impactThanks') }}</p>

      <section class="activity" aria-labelledby="reports-h">
        <div class="activity-head">
          <h2 id="reports-h">{{ $t('account.dashboard.yourReports') }}</h2>
        </div>
        <div v-if="reportSubmissions" class="filters" role="group" :aria-label="$t('account.dashboard.filterAria')">
          <button v-for="f in filters" :key="f.key" type="button" class="filter" :aria-pressed="filter === f.key ? 'true' : 'false'" @click="filter = f.key">
            {{ f.label }} · {{ f.count }}
          </button>
        </div>
        <ul class="report-list">
          <li v-for="report in visibleReports" :key="report.submission_id" class="card dash-report">
            <router-link :to="{ name: 'ViewSubmission', params: { lang: $i18n.locale, id: report.submission_id } }" class="report-top">
              <span class="thumb" aria-hidden="true">
                <img v-if="thumbUrl(report)" :src="thumbUrl(report)" alt="" />
                <i v-else class="fas fa-camera"></i>
              </span>
              <span class="report-info">
                <span class="chips">
                  <SeverityLabel :severity="report.severity_level" />
                  <span class="vis" :class="{ private: !report.report_public }">
                    <i :class="['fas', report.report_public ? 'fa-eye' : 'fa-eye-slash']" aria-hidden="true"></i>
                    {{ report.report_public ? $t('account.dashboard.public') : $t('account.dashboard.private') }}
                  </span>
                </span>
                <strong class="report-title">{{ reportTitle(report) }}</strong>
                <span class="meta"><i class="fas fa-calendar" aria-hidden="true"></i> {{ formatDate(report.created) }}</span>
              </span>
              <i class="fas fa-chevron-right chevron" aria-hidden="true"></i>
            </router-link>

            <div v-if="report.editMode" class="edit fade-in">
              <label class="sr-only" :for="'comment-' + report.id">{{ $t('account.dashboard.commentLabel') }}</label>
              <textarea :id="'comment-' + report.id" v-model="report.newComment" rows="4"></textarea>
              <div class="actions">
                <button type="button" class="action action--primary" @click="saveComment(report)">
                  <i class="fas fa-check" aria-hidden="true"></i> {{ $t('account.dashboard.saveChanges') }}
                </button>
                <button type="button" class="action" @click="cancelEdit(report)">
                  <i class="fas fa-xmark" aria-hidden="true"></i> {{ $t('common.actions.cancel') }}
                </button>
              </div>
            </div>
            <template v-else>
              <p class="comment">{{ report.comment }}</p>
              <div class="actions">
                <button type="button" class="action" @click="editComment(report)">
                  <i class="fas fa-pen" aria-hidden="true"></i> {{ $t('account.dashboard.editAction') }}
                </button>
                <button type="button" class="action" @click="toggleReportVisibility(report)">
                  <i :class="['fas', report.report_public ? 'fa-eye-slash' : 'fa-eye']" aria-hidden="true"></i>
                  {{ report.report_public ? $t('account.dashboard.makePrivateAction') : $t('account.dashboard.makePublicAction') }}
                </button>
              </div>
              <button type="button" class="action action--danger" @click="deleteSubmission(report)">
                <i class="fas fa-trash-can" aria-hidden="true"></i> {{ $t('account.dashboard.deleteAction') }}
              </button>
            </template>
          </li>
        </ul>
        <button v-if="reportsPage < reportsTotalPages" type="button" class="btn-standard load-more" :disabled="loadingReports" @click="loadMoreReports">
          {{ $t('account.dashboard.loadMore') }}
        </button>
      </section>
    </template>
  </div>
</template>

<script>
import { levelFor, LEVELS } from '@/utils/points';

import UserLogin from '@/components/UserLogin.vue';
import SeverityLabel from '@/components/SeverityLabel.vue';
import { mapState, mapActions } from 'vuex';


export default {
  name: "UserDashboard",

  components: {
    UserLogin,
    SeverityLabel,
  },

  data() {
    return {
      user: null,
      challenges: [
      ],
      userChallenges: [
      ],
      reportSubmissions: null,
      reportsPage: 1,
      reportsTotalPages: 1,
      loadingReports: false,
      shuffledChallenges: null,
      cities: null,
      currentChallengeIndex: 0,

      selectedChallenges: [], // Initialize to an empty array
      newComment: '',
      filter: 'all',


    };
  },
  computed: {
    ...mapState(['user']),
    loggedIn() {
    return !!localStorage.getItem('pocketbase_auth') || !!this.token;
  },
    recentSubmissions() {
    return this.user.submissions.slice(0, 5);
    },
    uniqueCities() {
    return [...new Set(this.reportSubmissions.map(report => report.city))];
  },
  cityCount() {
    return this.uniqueCities.length;
  },

    userAvatar() {
      if (this.user.model.avatar) {
        return `${process.env.VUE_APP_PB_URL}/api/files/_pb_users_auth_/${this.user.model.id}/${this.user.model.avatar}`;
      } else {
        return `https://ui-avatars.com/api/?name=${this.user.model.username}&background=random&size=200`;
      }
},

    visibleReports() {
      const list = this.reportSubmissions || [];
      if (this.filter === 'public') return list.filter((r) => r.report_public);
      if (this.filter === 'private') return list.filter((r) => !r.report_public);
      return list;
    },
    filters() {
      const list = this.reportSubmissions || [];
      const pub = list.filter((r) => r.report_public).length;
      return [
        { key: 'all', label: this.$t('account.dashboard.filterAll'), count: list.length },
        { key: 'public', label: this.$t('account.dashboard.public'), count: pub },
        { key: 'private', label: this.$t('account.dashboard.private'), count: list.length - pub },
      ];
    },
    userPoints() {
      return this.user.model.points || 0;
    },
    level() { return levelFor(this.userPoints); },
    pointsLog() {
      const d = this.user && this.user.model && this.user.model.data;
      return d && Array.isArray(d.points_log) ? [...d.points_log].reverse().slice(0, 8) : [];
    },
    LEVELS() { return LEVELS; },


  },

  mounted() {
    const params = new URL(window.location.href).searchParams;
    const code = params.get('code');
    const provider = JSON.parse(localStorage.getItem('provider'));

    if (code && provider) {
      this.authenticateWithCode(provider.name, code, provider.codeVerifier);
    }


  },

  created(){

    if (localStorage.getItem('pocketbase_auth')) {
      // Retrieve the user data from the authStore
      this.user = JSON.parse(localStorage.getItem('pocketbase_auth'));
    }

    // fetch challenge data from the authStore
    this.getReports();


  },
  watch: {
    loggedIn(newValue) {
      console.log('loggedIn changed to', newValue);
      if (newValue) {
        window.location.reload();
      }
    }
  },


  methods: {

    ...mapActions(['logout', 'updateUser']),
    // Reports have no title: the street (first part of the address) names the card.
    reportTitle(report) {
      const parts = (report.address || '').split(',').map((p) => p.trim()).filter(Boolean);
      return [parts[0], report.city].filter(Boolean).join(', ') || report.submission_id;
    },
    thumbUrl(report) {
      return report.image ? `${process.env.VUE_APP_PB_URL}/api/files/${report.collectionId}/${report.id}/${report.image}` : '';
    },
    formatDate(value) {
      const d = value && new Date(String(value).replace(' ', 'T'));
      return d && !isNaN(d) ? d.toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    },
    onLogoutClick() {
        this.logout({ $pb: this.$pb });
        this.$router.push(this.$lp('/'));
      },


      async authenticateWithCode(providerName, code, codeVerifier) {
        const redirectPath = '/profile';
        const redirectUrl = `${process.env.VUE_APP_URL}${redirectPath}`;

          try {
            const authData = await this.$pb.collection('users').authWithOAuth2Code(
              providerName,
              code,
              codeVerifier,
              redirectUrl,
              {
                emailVisibility: false,
              }
            );
            console.log(authData);
            window.location.reload();

            // Perform any necessary actions with the authentication data
          } catch (error) {
            console.error(error);
            // Handle error
          }
        },

      async getReports(page = 1) {
        const userId = this.user && this.user.model && this.user.model.id;
        if (!userId) return; // not signed in: nothing to load
        this.loadingReports = true;
        try {
          const resultList = await this.$pb.collection('reports').getList(page, 20, {
            filter: `user_og ~ '${userId}'`,
            sort: '-created',
            fields: 'id,collectionId,collectionName,submission_id,comment,severity_level,city,region,country,address,all_labels,report_public,created,timestamp,upvotes,title,image',
          });
          this.reportSubmissions = (page === 1 ? [] : this.reportSubmissions || []).concat(resultList.items);
          this.reportsPage = page;
          this.reportsTotalPages = resultList.totalPages;
          this.cities = [...new Set(this.reportSubmissions.map(report => report.city))];
        } finally {
          this.loadingReports = false;
        }
      },
      loadMoreReports() {
        return this.getReports(this.reportsPage + 1);
      },

      async editComment(submission) {
        submission.newComment = submission.comment;
        submission.editMode = true;
      },
      cancelEdit(submission) {
        submission.editMode = false;
      },



  async deleteSubmission(submission) {
    // Display a confirmation dialog to the user
    const confirmed = confirm(this.$t('account.dashboard.confirmDelete', { title: this.reportTitle(submission) }));
    if (!confirmed) {
      return;
    }

    try {
      // Delete the submission record from PocketBase
      await this.$pb.collection('reports').delete(submission.id);

      // Remove the submission from the list in the component's data
      const index = this.reportSubmissions.indexOf(submission);
      this.reportSubmissions.splice(index, 1);
    } catch (error) {
      console.error(error);
      alert(this.$t('account.dashboard.errDelete'));
    }
  },
  async toggleReportVisibility(report) {
  try {
    // Toggle the report_public field in PocketBase
    const newVisibility = !report.report_public;
    await this.$pb.collection('reports').update(report.id, { report_public: newVisibility });

    // Update the report_public field in the component's data
    report.report_public = newVisibility;
  } catch (error) {
    console.error(error);
    alert(this.$t('account.dashboard.errVisibility'));
  }
},
async saveComment(submission) {
  try {
    // Update the submission record in PocketBase
    await this.$pb.collection('reports').update(submission.id, { comment: submission.newComment });

    // Update the comment field in the component's data
    submission.comment = submission.newComment;
    submission.newComment = '';
    submission.editMode = false;
  } catch (error) {
    console.error(error);
    alert(this.$t('account.dashboard.errComment'));
  }
},





  },

};
</script>

<style scoped>
.profile-header { display: flex; align-items: center; gap: 12px; text-align: start; }
.profile-header img { width: 56px; height: 56px; flex: none; border-radius: 50%; object-fit: cover; }
.who { flex: 1; min-width: 0; }
.who h1 { margin: 0; font-size: 20px; text-align: start; overflow-wrap: anywhere; }
.muted { margin: 0; font-size: 14px; color: var(--color-secondary); overflow-wrap: anywhere; }
.icon-btn { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; border: 2px solid var(--color-border-input); color: var(--color-text); text-decoration: none; }
.icon-btn:hover { border-color: var(--color-primary-text); color: var(--color-primary-text); }

.stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 0; }
.stat { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 14px 8px; background: var(--color-subtle); border-radius: var(--radius-lg); }
.stat i { color: var(--color-primary-text); }
.stat dd { margin: 0; font-family: var(--font-mono); font-size: 20px; color: var(--color-text); }
.stat dt { font-family: var(--font-mono); font-size: 12px; color: var(--color-secondary); }
.thanks { margin: 0; font-size: 14px; color: var(--color-secondary); }

.filters { display: flex; flex-wrap: wrap; gap: 8px; }
.filter { min-height: 44px; padding: 0 16px; border-radius: var(--radius-pill); border: 2px solid var(--color-border-input); background: transparent; color: var(--color-text); font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
.filter[aria-pressed="true"] { background: var(--color-primary); border-color: var(--color-primary); color: var(--color-on-primary); }

.activity { display: flex; flex-direction: column; gap: 12px; }
.activity-head { display: flex; align-items: center; }
.activity-head h2 { margin: 0; text-align: start; }

.dash-report { display: flex; flex-direction: column; gap: 12px; }
.report-top { display: flex; align-items: flex-start; gap: 12px; color: inherit; text-decoration: none; }
.thumb { flex: none; display: flex; align-items: center; justify-content: center; width: 72px; height: 72px; border-radius: 12px; overflow: hidden; background: var(--color-subtle); color: var(--color-secondary); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.report-info { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
.chips { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.vis { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: var(--radius-pill); background: var(--color-subtle); color: var(--color-primary-text); font-size: 12px; }
.vis.private { color: var(--color-secondary); }
.report-title { font-size: 16px; overflow-wrap: anywhere; }
.meta { font-size: 14px; color: var(--color-secondary); }
.chevron { color: var(--color-secondary); margin-top: 4px; }
.comment { margin: 0; font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; border-top: 1px solid var(--color-border); padding-top: 12px; }

.edit { display: flex; flex-direction: column; gap: 12px; }
.edit textarea { width: 100%; box-sizing: border-box; padding: 12px; font: inherit; border: 2px solid var(--color-border-input); border-radius: 12px; background: var(--color-surface); color: inherit; }

.actions { display: flex; gap: 8px; }
.actions .action { flex: 1 1 0; }
.action { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; padding: 0 16px; box-sizing: border-box; border: 2px solid var(--color-border-input); border-radius: var(--radius-pill); background: transparent; color: var(--color-text); font: inherit; font-size: 14px; font-weight: 600; text-decoration: none; cursor: pointer; }
.action:hover { border-color: var(--color-primary-text); color: var(--color-primary-text); }
.action--primary { background: var(--color-primary); border-color: var(--color-primary); color: var(--color-on-primary); }
.action--primary:hover { color: var(--color-on-primary); filter: brightness(0.95); }
.action--danger { align-self: center; border-color: transparent; color: var(--color-error-text); }
.action--danger:hover { border-color: transparent; text-decoration: underline; color: var(--color-error-text); }
:global(body.dark-mode) .action { color: var(--color-text-light); }
:global(body.dark-mode) .action--primary { color: var(--color-on-primary); }
.load-more { display: block; margin: 16px auto 0; }
.level { display: flex; flex-direction: column; gap: 10px; margin: 16px 0; padding: 16px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); text-align: start; }
.level-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
.level-head h2 { margin: 0; font-size: 18px; }
.level .track { height: 12px; border-radius: 6px; background: var(--color-subtle); overflow: hidden; }
.level .fill { height: 100%; background: var(--color-primary); border-radius: 6px; }
.level .steps { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.level .steps li { font-size: 12px; padding: 4px 10px; border-radius: 999px; background: var(--color-subtle); color: var(--color-secondary); }
.level .steps li.on { background: var(--color-primary); color: var(--color-on-primary); }
.recent-points h3 { margin: 8px 0; font-size: 15px; }
.recent-points ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.recent-points li { display: flex; gap: 10px; align-items: center; justify-content: space-between; font-size: 14px; }
.recent-points li a { flex: 1; }
.level .muted { margin: 0; font-size: 13px; color: var(--color-secondary); }
</style>
